/* ============================================================
   BIONATURE — section interactions

   One delegated listener drives every BioNature interaction:
   carousel arrows, carousel dots, collapsibles and tab switchers.

   Loaded with `defer` by the sections that need it. The guard below
   makes duplicate tags harmless, and everything is scoped to its own
   section so repeated instances never clash.
   ============================================================ */
(function () {
  if (window.__bioNatureInit) return;
  window.__bioNatureInit = true;

  var GAP = 16;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function trackFor(el) {
    var id = el.getAttribute('data-bn-target');
    if (id) return document.getElementById(id);
    var scope = el.closest('[data-bn-carousel]');
    return scope ? scope.querySelector('.bn-carousel') : null;
  }

  function stepSize(track) {
    var card = track.firstElementChild;
    return card ? card.offsetWidth + GAP : 260;
  }

  /* ---------- Clicks ---------- */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    /* Carousel arrows / prev-next pills */
    var arrow = target.closest('[data-bn-scroll]');
    if (arrow) {
      var arrowTrack = trackFor(arrow);
      if (arrowTrack) {
        var delta = stepSize(arrowTrack);
        arrowTrack.scrollBy({
          left: arrow.getAttribute('data-bn-scroll') === 'next' ? delta : -delta,
          behavior: scrollBehavior(),
        });
      }
      return;
    }

    /* Carousel dots */
    var dot = target.closest('[data-bn-dot]');
    if (dot) {
      var dotTrack = trackFor(dot.parentElement) || trackFor(dot);
      if (dotTrack) {
        dotTrack.scrollTo({
          left: stepSize(dotTrack) * parseInt(dot.getAttribute('data-bn-dot'), 10),
          behavior: scrollBehavior(),
        });
      }
      return;
    }

    /* Collapsibles — reason cards and benefit accordions */
    var toggle = target.closest('[data-bn-toggle]');
    if (toggle) {
      var item = toggle.closest('[data-bn-collapsible]');
      if (item) {
        var open = item.classList.toggle('is-open');
        /* A flip card carries a button on each face and both describe the one
           disclosure, so the state goes on every toggle in the card, not just
           the one that was clicked. */
        item.querySelectorAll('[data-bn-toggle]').forEach(function (button) {
          if (button.hasAttribute('aria-expanded')) {
            button.setAttribute('aria-expanded', open ? 'true' : 'false');
          }
        });
        slidePanel(item, open);
      }
      return;
    }

    /* Tab switcher */
    var tab = target.closest('[data-bn-tab]');
    if (tab) {
      var switcher = tab.closest('[data-bn-switcher]');
      if (!switcher) return;
      var panelId = tab.getAttribute('data-bn-tab');

      switcher.querySelectorAll('[data-bn-tab]').forEach(function (button) {
        var active = button === tab;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-expanded', active ? 'true' : 'false');
      });

      switcher.querySelectorAll('[data-bn-panel]').forEach(function (panel) {
        var active = panel.getAttribute('data-bn-panel') === panelId;
        panel.classList.toggle('is-active', active);
        /* The class is the whole switch. `hidden` used to be set here too,
           and display: none cannot be animated — the stylesheet fades these
           with visibility instead, which hides a panel just as completely
           from a screen reader and from the tab order. */
      });
    }
  });

  /* ---------- Slide a panel open and shut ----------
     A stylesheet can only animate max-height towards a number it was given up
     front, and a guessed number either clips long copy or stalls short copy
     against a transition that has nowhere left to travel. Measuring the panel
     and animating to that height makes any length of text behave the same.

     Panels without the attribute keep whatever the stylesheet does with
     .is-open, so the older accordions are untouched. */
  function slidePanel(item, open) {
    var panel = item.querySelector('[data-bn-collapsible-body]');
    if (!panel) return;

    if (prefersReducedMotion()) {
      panel.style.maxHeight = open ? 'none' : '0px';
      return;
    }

    if (open) {
      panel.style.maxHeight = panel.scrollHeight + 'px';
      panel.addEventListener('transitionend', function done(event) {
        if (event.propertyName !== 'max-height') return;
        panel.removeEventListener('transitionend', done);
        // Released once the animation is over, so the panel can reflow with
        // the window instead of staying pinned to a stale measurement.
        if (item.classList.contains('is-open')) panel.style.maxHeight = 'none';
      });
    } else {
      // From 'none' there is nothing to animate away from, so pin the current
      // height for a frame first.
      panel.style.maxHeight = panel.scrollHeight + 'px';
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          panel.style.maxHeight = '0px';
        });
      });
    }
  }

  /* ---------- Advance the track on its own ----------
     Stops on hover, on focus inside the track and on touch, so it never
     fights someone who is reading or scrolling. Reduced motion opts out
     entirely rather than jumping between cards. */
  function bindAutoplay(root) {
    (root || document).querySelectorAll('[data-bn-autoplay]').forEach(function (track) {
      if (track.dataset.bnAutoplayBound === 'true') return;
      track.dataset.bnAutoplayBound = 'true';
      if (prefersReducedMotion()) return;

      var delay = parseInt(track.getAttribute('data-bn-autoplay'), 10);
      if (!delay || delay < 1000) return;

      var timer = null;
      var paused = false;

      function advance() {
        if (paused || !track.isConnected) return;
        var step = stepSize(track);
        var end = track.scrollWidth - track.clientWidth;
        var next = track.scrollLeft + step;
        track.scrollTo({ left: next > end - 4 ? 0 : next, behavior: scrollBehavior() });
      }

      function start() {
        if (timer) return;
        timer = window.setInterval(advance, delay);
      }

      function stop() {
        window.clearInterval(timer);
        timer = null;
      }

      var scope = track.closest('[data-bn-carousel]') || track;
      ['mouseenter', 'focusin', 'touchstart'].forEach(function (name) {
        scope.addEventListener(name, function () {
          paused = true;
        }, { passive: true });
      });
      ['mouseleave', 'focusout'].forEach(function (name) {
        scope.addEventListener(name, function () {
          paused = false;
        }, { passive: true });
      });

      // A tab in the background should not queue up a burst of scrolls.
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      start();
    });
  }

  bindAutoplay(document);

  /* ---------- Keep dots in sync while the track scrolls ---------- */
  function bindDots(root) {
    (root || document).querySelectorAll('[data-bn-dots]').forEach(function (dots) {
      if (dots.dataset.bnBound === 'true') return;
      var track = trackFor(dots);
      if (!track) return;
      dots.dataset.bnBound = 'true';

      track.addEventListener(
        'scroll',
        function () {
          var index = Math.round(track.scrollLeft / stepSize(track));
          dots.querySelectorAll('[data-bn-dot]').forEach(function (button, i) {
            button.classList.toggle('is-active', i === index);
          });
        },
        { passive: true }
      );
    });
  }

  /* ---------- Hide navigation that has nothing to move ----------
     A track whose cards all fit has nothing to scroll, so its arrows and
     pills sit there doing nothing when clicked — which reads as broken
     rather than as "you have seen everything". They are hidden until the
     track actually overflows, and rechecked on resize, since how many
     cards fit is a question of width. */
  function syncNav(root) {
    (root || document).querySelectorAll('[data-bn-scroll]').forEach(function (button) {
      var track = trackFor(button);
      if (!track) return;
      button.hidden = track.scrollWidth <= track.clientWidth + 1;
    });
  }

  /* ---------- Level the accordion heads ----------
     A row whose tags wrap onto a second line stands taller than the rest and
     the list reads as ragged. The tallest head in a list sets the height for
     all of them.

     Measured rather than given a min-height in the stylesheet: the tags are
     the merchant's own words, and how many lines they take is a question of
     both their length and the width of the screen. The height is cleared
     before measuring so that a wider window can give the space back instead
     of holding the row at whatever the narrowest layout needed. */
  function levelHeads(root) {
    (root || document).querySelectorAll('[data-bn-level-heads]').forEach(function (list) {
      var heads = list.querySelectorAll('.bn-benefit__head');
      if (!heads.length) return;

      var tallest = 0;
      heads.forEach(function (head) {
        head.style.minHeight = '';
      });
      heads.forEach(function (head) {
        tallest = Math.max(tallest, head.offsetHeight);
      });
      heads.forEach(function (head) {
        head.style.minHeight = tallest + 'px';
      });
    });
  }

  bindDots(document);
  syncNav(document);
  levelHeads(document);

  /* The first measurement runs against whatever font is showing. Once the
     real one arrives the words are a different width, and a row that fitted
     on one line may no longer. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      levelHeads(document);
    });
  }

  var resizeQueued = false;
  window.addEventListener(
    'resize',
    function () {
      if (resizeQueued) return;
      resizeQueued = true;
      window.requestAnimationFrame(function () {
        resizeQueued = false;
        syncNav(document);
        levelHeads(document);
      });
    },
    { passive: true }
  );

  /* Theme editor re-renders sections without a page reload */
  document.addEventListener('shopify:section:load', function (event) {
    bindDots(event.target);
    bindAutoplay(event.target);
    syncNav(event.target);
    levelHeads(event.target);
  });
})();
